import type { Parcel } from '@/types'

/**
 * EP-67 derived parcel status.
 *
 * Combines the lifecycle `status` (pending/notified/collected) with the photo
 * `extraction_status` so a single badge tells staff what to do next:
 *  - a parcel that extraction could not auto-resolve to a unit (and has no unit
 *    yet) surfaces as "Needs review" so it can be picked up in the batch tab.
 *
 * These helpers are pure (React-free) and return an i18n KEY + Badge variant —
 * the caller translates. Only invoke them when the parcel-extraction dual-gate
 * is on (`useParcelExtractionEnabled()`); otherwise render the plain
 * `parcel.status` badge.
 */

export type ParcelStatusVariant = 'success' | 'info' | 'warning' | 'error' | 'default'

export interface ParcelDisplayStatus {
  labelKey: string
  labelOpts?: Record<string, string>
  variant: ParcelStatusVariant
  needsReview: boolean
}

/**
 * A parcel "needs review" when extraction did NOT auto-assign a unit and none is
 * set yet — both `needs_review` (candidates found) and `no_match` (unreadable).
 * `matched` auto-sets `unit_id`, and assigning a unit manually clears the flag.
 */
export function isNeedsReview(parcel: Parcel): boolean {
  return (
    parcel.unit_id == null &&
    (parcel.extraction_status === 'needs_review' || parcel.extraction_status === 'no_match')
  )
}

export function getParcelDisplayStatus(parcel: Parcel): ParcelDisplayStatus {
  if (isNeedsReview(parcel)) {
    return { labelKey: 'status.needs_review', variant: 'error', needsReview: true }
  }
  if (parcel.extraction_status === 'processing') {
    return { labelKey: 'extraction.reading_unit', variant: 'default', needsReview: false }
  }
  switch (parcel.status) {
    case 'collected':
      return { labelKey: 'status.collected', variant: 'success', needsReview: false }
    case 'notified':
      return { labelKey: 'status.notified', variant: 'info', needsReview: false }
    default:
      return { labelKey: 'status.pending', variant: 'warning', needsReview: false }
  }
}
